import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `class VersionedStore<V> {
  /** Record that \`key\` had \`value\` from \`timestamp\` onwards. */
  set(key: string, value: V, timestamp: number): void {
    // TODO
  }

  /** The value \`key\` had at \`timestamp\`, or undefined if it had none yet. */
  get(key: string, timestamp: number): V | undefined {
    // TODO
    return undefined;
  }
}
`;

const solution = `type Version<V> = { at: number; value: V };

class VersionedStore<V> {
  private history = new Map<string, Version<V>[]>();

  set(key: string, value: V, timestamp: number): void {
    const versions = this.history.get(key) ?? [];
    // Writes usually arrive in order, so check the cheap case first and only
    // pay for a search when they do not.
    const last = versions[versions.length - 1];
    if (last === undefined || timestamp > last.at) {
      versions.push({ at: timestamp, value });
    } else if (timestamp === last.at) {
      last.value = value;
    } else {
      const index = this.firstAfter(versions, timestamp);
      if (versions[index]?.at === timestamp) versions[index] = { at: timestamp, value };
      else versions.splice(index, 0, { at: timestamp, value });
    }
    this.history.set(key, versions);
  }

  get(key: string, timestamp: number): V | undefined {
    const versions = this.history.get(key);
    if (!versions || versions.length === 0) return undefined;
    // The version in effect is the last one written at or before this instant,
    // so find the first strictly after and step back one.
    const index = this.firstAfter(versions, timestamp) - 1;
    return index >= 0 ? versions[index]!.value : undefined;
  }

  // Index of the first version strictly after \`timestamp\`. Binary search, so a
  // key with a long history costs log(n) per read rather than n. That is the
  // difference between a store you can query and one you cannot.
  private firstAfter(versions: Version<V>[], timestamp: number): number {
    let low = 0;
    let high = versions.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (versions[mid]!.at <= timestamp) low = mid + 1;
      else high = mid;
    }
    return low;
  }
}`;

const harness = HARNESS_KIT + `
if (typeof VersionedStore !== "function") {
  throw new Error("VersionedStore is not defined. Keep the class name as given.");
}

const __tests = [
  {
    name: "reads back a value at the instant it was written",
    about: "The at-or-before rule includes the instant itself.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "one", 10);
      __expectExact("set(a,'one',10); get(a,10)", function () { return s.get("a", 10); }, "one");
    }
  },
  {
    name: "reads the value in effect between writes",
    about: "A read lands between two writes far more often than exactly on one.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "one", 10);
      s.set("a", "two", 20);
      __expectExact("get(a,15)", function () { return s.get("a", 15); }, "one");
      __expectExact("get(a,25)", function () { return s.get("a", 25); }, "two");
    }
  },
  {
    name: "before the first write there is no value",
    about: "The key exists in the store but had no value yet at that instant.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "one", 10);
      __expectExact("get(a,5)", function () { return s.get("a", 5); }, undefined);
    }
  },
  {
    name: "an unknown key is undefined",
    about: "Never written is different from written later.",
    run: function () {
      const s = new VersionedStore();
      __expectExact("get('nope', 1)", function () { return s.get("nope", 1); }, undefined);
    }
  },
  {
    name: "keys are independent",
    hidden: true,
    about: "One shared history means every key returns the last value written to any key.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "A", 10);
      s.set("b", "B", 11);
      __expectExact("get(a,20)", function () { return s.get("a", 20); }, "A");
      __expectExact("get(b,20)", function () { return s.get("b", 20); }, "B");
    }
  },
  {
    name: "the latest value is returned long after the last write",
    hidden: true,
    about: "A value stays in effect until something replaces it. A search that only matches an exact timestamp returns nothing here.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "one", 10);
      __expectExact("get(a, 999999)", function () { return s.get("a", 999999); }, "one");
    }
  },
  {
    name: "writing the same timestamp twice replaces rather than duplicates",
    hidden: true,
    about: "Two values cannot both be in effect at one instant. Appending both leaves the answer depending on which the search happens to land on.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "first", 10);
      s.set("a", "second", 10);
      __expectExact("two writes at t=10; get(a,10)", function () { return s.get("a", 10); }, "second");
      __expectExact("... get(a,50)", function () { return s.get("a", 50); }, "second");
    }
  },
  {
    name: "an out-of-order write lands in the right place",
    hidden: true,
    about: "Backfill and replayed events arrive late. Appending blindly leaves the history unsorted, and a binary search over unsorted data returns a confidently wrong answer rather than an error.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "late", 30);
      s.set("a", "early", 10);
      __expectExact("wrote t=30 then t=10; get(a,20)", function () { return s.get("a", 20); }, "early");
      __expectExact("... get(a,35)", function () { return s.get("a", 35); }, "late");
      __expectExact("... get(a,5)", function () { return s.get("a", 5); }, undefined);
    }
  },
  {
    name: "a long history is still correct at every boundary",
    hidden: true,
    about: "Binary search is where off-by-ones live. Checking every boundary and the gaps between them is what catches a bound that is one out.",
    run: function () {
      const s = new VersionedStore();
      for (let i = 1; i <= 50; i++) s.set("k", "v" + i, i * 10);
      for (let i = 1; i <= 50; i++) {
        __expectExact("get(k, " + (i * 10) + ") exactly on write " + i, function () {
          return s.get("k", i * 10);
        }, "v" + i);
        __expectExact("get(k, " + (i * 10 + 5) + ") just after write " + i, function () {
          return s.get("k", i * 10 + 5);
        }, "v" + i);
      }
    }
  },
  {
    name: "handles a timestamp of zero",
    hidden: true,
    about: "Zero is falsy, so any check written as 'if (timestamp)' silently treats the epoch as no timestamp at all.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", "zero", 0);
      __expectExact("set at t=0; get(a,0)", function () { return s.get("a", 0); }, "zero");
      __expectExact("... get(a,1)", function () { return s.get("a", 1); }, "zero");
    }
  },
  {
    name: "stores falsy values faithfully",
    hidden: true,
    about: "A stored 0, empty string or false must come back as itself. Returning a value only 'if it is truthy' loses all three.",
    run: function () {
      const s = new VersionedStore();
      s.set("a", 0, 10);
      s.set("b", "", 10);
      s.set("c", false, 10);
      __expectExact("get(a,10)", function () { return s.get("a", 10); }, 0);
      __expectExact("get(b,10)", function () { return s.get("b", 10); }, "");
      __expectExact("get(c,10)", function () { return s.get("c", 10); }, false);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "The history for a key is in time order. What does that let you do instead of scanning backwards through it? Write the search once, as its own function, because both get and set need the same answer.",
    code: `type Version<V> = { at: number; value: V };

// Index of the first version strictly AFTER timestamp. Everything else is
// phrased in terms of this, so there is only one off-by-one to get right.
private firstAfter(versions: Version<V>[], timestamp: number): number {
  let low = 0;
  let high = versions.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (versions[mid]!.at <= timestamp) low = mid + 1;   // <=, so an exact hit is "at or before"
    else high = mid;
  }
  return low;
}`,
  },
  {
    label: "Approach",
    body: "get finds the first version strictly after the instant and steps back one. set takes the cheap path when the write is the newest, which it usually is, and only pays for a search when it is not.",
    code: `get(key: string, timestamp: number): V | undefined {
  const versions = this.history.get(key);
  if (!versions || versions.length === 0) return undefined;
  const index = this.firstAfter(versions, timestamp) - 1;
  // index, not the value, is tested: a stored 0, "" or false must come back.
  return index >= 0 ? versions[index]!.value : undefined;
}

set(key: string, value: V, timestamp: number): void {
  const versions = this.history.get(key) ?? [];
  const last = versions[versions.length - 1];
  if (last === undefined || timestamp > last.at) versions.push({ at: timestamp, value });
  else if (timestamp === last.at) last.value = value;      // one instant, one value
  else {
    const index = this.firstAfter(versions, timestamp);    // a late write, spliced into place
    if (versions[index]?.at === timestamp) versions[index] = { at: timestamp, value };
    else versions.splice(index, 0, { at: timestamp, value });
  }
  this.history.set(key, versions);
}`,
  },
  {
    label: "The trap",
    body: "The last one at or before t is the classic off-by-one, so decide which side your search settles on before you write it. A late write appended blindly leaves the history unsorted, at which point the binary search does not fail, it returns the wrong value confidently. And zero is falsy twice over here: as a timestamp and as a stored value.",
  },
];

export const versionedStore: Drill = {
  id: "versioned-store",
  track: "typescript",
  title: "Versioned key-value store",
  pattern: "binary-search",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `Feature flags change over time, and when something breaks you need to know what a flag was set to *at the moment of the incident*, not now. Build a store that keeps every version.

\`\`\`ts
class VersionedStore<V> {
  set(key: string, value: V, timestamp: number): void;
  get(key: string, timestamp: number): V | undefined;
}
\`\`\`

\`get\` returns the value in effect at that instant: the most recent write **at or before** the timestamp.

## Requirements

- **At or before**, so reading exactly on a write returns that write.
- **Before the first write**, and for an unknown key, return \`undefined\`.
- **A value stays in effect** until something replaces it, however long that is.
- **Writes can arrive out of order** and must still be found correctly.
- **Two writes at the same timestamp**: the later call wins.

## Example

\`\`\`ts
const flags = new VersionedStore<boolean>();
flags.set("checkout-v2", false, 100);
flags.set("checkout-v2", true, 200);

flags.get("checkout-v2", 150);   // false — the incident was at 150
flags.get("checkout-v2", 200);   // true
flags.get("checkout-v2", 50);    // undefined
flags.get("checkout-v2", 9999);  // true
\`\`\`

## Where this gets interesting

A linear scan backwards through the history is correct, and it is the answer that gets a follow-up: with a key written a million times, every read walks a million entries. The history is sorted by time, so a binary search brings a read to log(n). Expect to be asked for it, and expect the off-by-one, because "the last one at or before t" is the classic place to be one index out.

The other half is writes arriving late. Backfill and replayed events are ordinary, and appending them blindly leaves the history unsorted, at which point a binary search does not error, it just returns the wrong value with total confidence.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "get is O(log n) in the number of versions for that key. set is O(1) amortised for the ordinary in-order case, O(n) for a late write that has to be inserted.",
};
